import React from 'react';
import {
  Toast,
  Toaster as ChakraToaster,
  createToaster
} from '@chakra-ui/react';

import { AppNotification, NotificationBox } from './Notifications';

/**
 * Singleton toast store for the app. Replaces Chakra v2's `useToast` hook.
 *
 * Usage:
 *   import { toaster } from '$components/Toaster';
 *   toaster.create({ id, title, type: 'success', duration: 5000 });
 *   toaster.update(id, { title, type: 'success' });
 *   toaster.dismiss(id);   // dismiss one
 *   toaster.dismiss();     // dismiss all
 *   toaster.isVisible(id);
 *
 * For custom-rendered toasts (e.g. the notifications panel), set
 * `meta: { kind: 'notifications', notifications }`. The render function in
 * `<Toaster />` switches on `meta.kind` to choose the JSX shape.
 */
export const toaster = createToaster({
  placement: 'bottom-end',
  pauseOnPageIdle: true
});

interface NotificationsToastMeta {
  kind: 'notifications';
  notifications: AppNotification[];
}

function isNotificationsMeta(
  meta: Record<string, any> | undefined
): meta is NotificationsToastMeta {
  return !!meta && meta.kind === 'notifications';
}

/**
 * Mounts the toast region once near the root of the app. Renders custom JSX
 * for `meta.kind === 'notifications'` toasts; otherwise renders the default
 * Toast.Root / Title / Description / CloseTrigger shape.
 */
export function Toaster() {
  return (
    <ChakraToaster toaster={toaster}>
      {(toast) => {
        if (isNotificationsMeta(toast.meta)) {
          // The toast group is `position: fixed` with no width and its roots
          // are absolutely positioned, so the group collapses to 0 width and
          // the recipe's `width: full` resolves to 0. Give the root an explicit
          // width (Chakra's docs do the same) and drop the recipe padding/bg so
          // NotificationBox — which brings its own surface — fills it edge to
          // edge.
          return (
            <Toast.Root
              width={{ base: 'calc(100vw - 2rem)', md: 'md' }}
              p={0}
              bg='transparent'
              boxShadow='none'
            >
              <NotificationBox
                notifications={toast.meta.notifications}
                onCloseClick={() => toaster.dismiss(toast.id)}
              />
            </Toast.Root>
          );
        }

        return (
          <Toast.Root width={{ base: 'calc(100vw - 2rem)', md: 'sm' }}>
            {/*
             * v3 no longer auto-renders an icon for `type: 'loading' |
             * 'success' | 'error' | 'warning' | 'info'`. <Toast.Indicator />
             * picks the correct indicator off the toast type, so include it
             * before the text content.
             */}
            <Toast.Indicator />
            {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
            {toast.description && (
              <Toast.Description>{toast.description}</Toast.Description>
            )}
            {/*
             * Opt-in close button (v2 `isClosable` semantics). Don't render
             * it by default: the infinite-duration loading toasts are updated
             * in place on completion, and dismissing one mid-flight would
             * make that update target a removed toast — the success
             * confirmation would never appear.
             */}
            {toast.closable && <Toast.CloseTrigger />}
          </Toast.Root>
        );
      }}
    </ChakraToaster>
  );
}
